import { type ExtractPayload, reducedStream, reducer } from '@ardoq/rxbeach';
import {
  DashboardApiError,
  DashboardTrackingEventsNames,
  EditModeDashboardDataState,
  type LoadedDashboard,
} from '@ardoq/dashboard';
import {
  clearDashboardData,
  editWidgetLinkModeDashboardFilterBinding,
  saveDashboard,
  setDashboardApiError,
  SetDashboardApiErrorPayload,
  setDashboardDescription,
  setDashboardLoadingState,
  setDashboardName,
  setLinkModeDashboardFilterParameterId,
  setLoadedDashboard,
  setLoadingDataSources,
  setDashboardFolderId,
  setShowLinkChartPopover,
  setShowValidationErrors,
  widgetFilterDrawerClosed,
  widgetFilterDrawerOpened,
} from './actions';
import { ArdoqId, HexColor } from '@ardoq/api-types';
import { LoadingState } from '../types';
import { trackEvent } from 'tracking/tracking';
import { constant, omit } from 'lodash';
import { Maybe, setStateProperty, updateState } from '@ardoq/common-helpers';
import { qualitativeTheme } from '@ardoq/color-picker';

// ============================================================================
// TYPES AND STATE DEFINITION
// ============================================================================

export type DashboardBuilder$State = Omit<
  EditModeDashboardDataState,
  | 'widgets'
  | 'availableDataSources'
  | 'selectedWidgetId'
  | 'settings'
  | 'globalFilterParameters'
  | 'globalFilterValues'
  | 'paletteColors'
  | 'periodStart'
  | 'periodEnd'
  | 'validationErrors'
> & {
  loadingState: LoadingState | null;
  showWidgetValidationProblemsAsErrors: boolean;
  linkModeDashboardFilterParameterId: ArdoqId | null;
  loadingDataSources: boolean;
  apiError?: DashboardApiError;
  isFilterDrawerOpened: boolean;
  selectedColorPalette: HexColor[];
  showLinkChartPopover: boolean;
  folderId: Maybe<ArdoqId>;
};

const defaultState: DashboardBuilder$State = {
  description: '',
  isFilterDrawerOpened: false,
  loadingDataSources: false,
  loadingState: null,
  name: '',
  showWidgetValidationProblemsAsErrors: false,
  selectedColorPalette: qualitativeTheme.paletteColors,
  linkModeDashboardFilterParameterId: null,
  folderId: null,
  scenarioBranchIds: null,
  showLinkChartPopover: true,
};

// ============================================================================
// DASHBOARD PROPERTIES
// ============================================================================

const handleSetDashboardName = (
  state: DashboardBuilder$State,
  name: string
): DashboardBuilder$State => {
  if (!state.name && name) {
    trackEvent(DashboardTrackingEventsNames.ADDED_NAME_TO_DASHBOARD);
  }
  return {
    ...state,
    name,
  };
};

const handleSetDashboardDescription = (
  state: DashboardBuilder$State,
  newDashboardDescription: string
): DashboardBuilder$State => {
  if (!state.description && newDashboardDescription) {
    trackEvent(DashboardTrackingEventsNames.ADDED_DESCRIPTION_TO_DASHBOARD);
  }
  return {
    ...state,
    description: newDashboardDescription,
  };
};

const handleSetLoadedDashboard = (
  state: DashboardBuilder$State,
  loadedDashboard: LoadedDashboard
): DashboardBuilder$State => ({
  ...state,
  apiError: undefined,
  loadingState: null,
  ...omit(loadedDashboard, [
    'widgets',
    'settings',
    'globalFilterParameters',
    'globalFilterValues',
  ]),
  linkModeDashboardFilterParameterId: null,
});

const handleSetIsDashboardLoading = (
  state: DashboardBuilder$State,
  { apiLoadingState }: ExtractPayload<typeof setDashboardLoadingState>
): DashboardBuilder$State => ({
  ...state,
  apiError: undefined,
  loadingState:
    state.loadingState === LoadingState.SAVING &&
    apiLoadingState === LoadingState.LOADING
      ? state.loadingState
      : apiLoadingState,
});

// ============================================================================
// VALIDATION AND ERROR HANDLING
// ============================================================================

const handleSetApiError = (
  state: DashboardBuilder$State,
  {
    apiError,
    apiLoadingState = LoadingState.LOADING_ERROR,
  }: SetDashboardApiErrorPayload
): DashboardBuilder$State => ({
  ...state,
  loadingState: apiLoadingState,
  apiError,
});

// ============================================================================
// STREAM DEFINITION
// ============================================================================

export const dashboardBuilder$ = reducedStream<DashboardBuilder$State>(
  'dashboardBuilder$',
  defaultState,
  [
    reducer(clearDashboardData, constant(defaultState)),
    reducer(setDashboardApiError, handleSetApiError),
    reducer(setDashboardDescription, handleSetDashboardDescription),
    reducer(setDashboardLoadingState, handleSetIsDashboardLoading),
    reducer(setDashboardName, handleSetDashboardName),
    reducer(setLoadedDashboard, handleSetLoadedDashboard),
    reducer(setLoadingDataSources, setStateProperty('loadingDataSources')),
    reducer(
      setShowValidationErrors,
      updateState({ showWidgetValidationProblemsAsErrors: true })
    ),
    reducer(
      saveDashboard,
      updateState({ showWidgetValidationProblemsAsErrors: false }) // If save has been triggered, that means that validation passed and we should reset this flag
    ),
    reducer(
      widgetFilterDrawerClosed,
      updateState({ isFilterDrawerOpened: false })
    ),
    reducer(
      widgetFilterDrawerOpened,
      updateState({ isFilterDrawerOpened: true })
    ),
    reducer(
      editWidgetLinkModeDashboardFilterBinding,
      updateState({ isFilterDrawerOpened: true })
    ),
    reducer(
      setLinkModeDashboardFilterParameterId,
      setStateProperty('linkModeDashboardFilterParameterId')
    ),
    reducer(setShowLinkChartPopover, setStateProperty('showLinkChartPopover')),
    reducer(setDashboardFolderId, setStateProperty('folderId')),
  ]
);
